import { IMatchConfigEntry, IMatchConfigEntryZr, MatchTypeWire, PlayerGrouping } from '@nova/types';

import { MatchConfigRepository } from './match-configs.repository';

export class MatchConfigService {
  private _matchConfigRepo: MatchConfigRepository;

  public constructor(matchConfigRepository?: MatchConfigRepository) {
    this._matchConfigRepo = matchConfigRepository || new MatchConfigRepository();
  }

  public async init(gameAPIEndpoint: string): Promise<void> {
    await this._matchConfigRepo.init(gameAPIEndpoint);
  }

  public getAll(): (IMatchConfigEntry | IMatchConfigEntryZr)[] {
    const orMatchConfigs = this._matchConfigRepo.getOrCopied().filter((matchConfig) => matchConfig.isAvailable);
    const zrMatchConfigs = this._matchConfigRepo.getZrCopied().filter((matchConfig) => matchConfig.isAvailable);
    const tfMatchConfigs = this._matchConfigRepo.getTfCopied().filter((matchConfig) => matchConfig.isAvailable);
    return [...orMatchConfigs, ...zrMatchConfigs, ...tfMatchConfigs];
  }

  public findById(matchConfigId: string): IMatchConfigEntry | IMatchConfigEntryZr {
    const matchConfig = this._matchConfigRepo.findOrById(matchConfigId)
      || this._matchConfigRepo.findZrById(matchConfigId)
      || this._matchConfigRepo.findTfById(matchConfigId);
    if (!matchConfig) { throw new Error(`Could not find match config <${matchConfigId}>`); }
    return matchConfig;
  }

  /** Per-team cap for one lobby party (`matchConfig.teamSize`). Not total match capacity. */
  public getPlayerCap(matchConfig: IMatchConfigEntry): number {
    return matchConfig.teamSize;
  }

  /** Largest per-team cap among all available match configs. */
  public getTotalPlayerCap(): number {
    const matchConfigs = this.getAll();

    return this.getMaxPlayerCap(matchConfigs);
  }

  public getMultiplayer(matchType?: MatchTypeWire): IMatchConfigEntry[] {
    const matchConfigs = this.getAll().filter((matchConfig) => matchConfig.playerGrouping !== PlayerGrouping.SP);
    if (!matchType) {
      return matchConfigs;
    }
    return matchConfigs.filter((matchConfig) => matchConfig.type === matchType);
  }

  private getMaxPlayerCap(matchConfigs: IMatchConfigEntry[]): number {
    let runningPlayerCap = 1;

    for (const matchConfig of matchConfigs) {
      const matchConfigPlayerCap = this.getPlayerCap(matchConfig);

      if (matchConfigPlayerCap > runningPlayerCap) {
        runningPlayerCap = matchConfigPlayerCap;
      }
    }

    return runningPlayerCap;
  }

  /** Largest per-team cap among available multiplayer match configs (optionally for one match type). */
  public getMultiplayerPlayerCap(matchType?: MatchTypeWire): number {
    const matchConfigs = this.getMultiplayer(matchType);
    return this.getMaxPlayerCap(matchConfigs);
  }

  /** Match config IDs where a single lobby of `playerCount` fits within one team (`teamSize >= playerCount`). */
  public getIdsByPlayerCount(playerCount: number): string[] {
    const allMatchConfigs = this.getAll();
    const filteredMatchConfigs = allMatchConfigs.filter((matchConfig) => matchConfig.teamSize >= playerCount);
    return filteredMatchConfigs.map((matchConfig) => matchConfig.id);
  }

  /** True when one lobby's headcount (`playerCount`) does not exceed the match config's per-team `teamSize`. */
  public isPlayerCountValid(matchConfigId: string, playerCount: number): boolean {
    const matchConfig = this.findById(matchConfigId);
    if (!matchConfig) {
      throw new Error(`MatchConfigService::isPlayerCountValid: Match config ${matchConfigId} not found`);
    }

    const playerCountOverTeamSize = playerCount > matchConfig.teamSize;
    if (playerCountOverTeamSize) {
      console.error(
        `MatchConfigService::isPlayerCountValid: Player count <${playerCount}> over team size <${matchConfig.teamSize}> ` +
        `for match config <${matchConfigId}>.`
      );
      return false;
    }

    return true;
  }
}
